import React, { useState, useEffect } from 'react';
import { FileText, Plus, Truck, CheckCircle } from 'lucide-react';
import barrelReplenishmentService from '../../../services/barrelReplenishmentService';

const PurchaseOrdersPanel = ({ refreshKey, onCreateNew }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadOrders();
  }, [refreshKey, filter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const filters = filter !== 'all' ? { status: filter } : {};
      const data = await barrelReplenishmentService?.getPurchaseOrders(filters);
      setOrders(data);
    } catch (error) {
      console.error('Error loading purchase orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await barrelReplenishmentService?.updatePurchaseOrder(orderId, { status: newStatus });
      await loadOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      sent: 'bg-purple-100 text-purple-700',
      confirmed: 'bg-indigo-100 text-indigo-700',
      in_transit: 'bg-orange-100 text-orange-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return badges?.[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      approved: 'Approuvé',
      sent: 'Envoyé',
      confirmed: 'Confirmé',
      in_transit: 'En transit',
      delivered: 'Livré',
      cancelled: 'Annulé'
    };
    return labels?.[status] || status;
  };

  const getBarrelTypeLabel = (type) => {
    return type === 'noir_270l' ? 'Noir 270L' : 'Bleu 220L';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          Bons de Commande
        </h2>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tous</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvés</option>
            <option value="in_transit">En transit</option>
            <option value="delivered">Livrés</option>
          </select>
          <button
            onClick={onCreateNew}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nouvelle Commande
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">N° BC</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fournisseur</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Quantité</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Montant</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Livraison</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {orders?.map((order) => (
              <tr key={order?.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{order?.po_number}</div>
                  <div className="text-xs text-gray-500">
                    {new Date(order?.order_date)?.toLocaleDateString('fr-FR')}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-900">{order?.barrel_suppliers?.name || 'N/A'}</div>
                  <div className="text-xs text-gray-500">{order?.barrel_suppliers?.city || ''}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-gray-900">
                    {getBarrelTypeLabel(order?.barrel_type)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-gray-900">{order?.quantity} unités</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-bold text-gray-900">{order?.total_amount?.toFixed(2)}€</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(order?.status)}`}>
                    {getStatusLabel(order?.status)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-900">
                    {order?.expected_delivery_date
                      ? new Date(order?.expected_delivery_date)?.toLocaleDateString('fr-FR')
                      : 'Non défini'}
                  </div>
                  {order?.actual_delivery_date && (
                    <div className="text-xs text-green-600">
                      Livré le {new Date(order?.actual_delivery_date)?.toLocaleDateString('fr-FR')}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {order?.status === 'pending' && (
                      <button
                        onClick={() => handleStatusUpdate(order?.id, 'approved')}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        title="Approuver"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                    {order?.status === 'approved' && (
                      <button
                        onClick={() => handleStatusUpdate(order?.id, 'sent')}
                        className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                        title="Marquer comme envoyé"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                    )}
                    {(order?.status === 'sent' || order?.status === 'confirmed') && (
                      <button
                        onClick={() => handleStatusUpdate(order?.id, 'in_transit')}
                        className="p-1 text-orange-600 hover:bg-orange-100 rounded"
                        title="En transit"
                      >
                        <Truck className="w-4 h-4" />
                      </button>
                    )}
                    {order?.status === 'in_transit' && (
                      <button
                        onClick={() => handleStatusUpdate(order?.id, 'delivered')}
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                        title="Marquer comme livré"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {orders?.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>Aucun bon de commande</p>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrdersPanel;